import './ApplicationPrioritizationWizard.scss';

import { CriticalityMap } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Grid, Spinner } from '@amzn/awsui-components-react';
import { MutableRefObject, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../common/Localization';
import { deepCopy } from '../../../common/util';
import { AttributeSelectionOption } from './ApplicationPrioritization';

type Props = FlashContextInterface & {
    attributes: AttributeSelectionOption[];
    setDisableSubmitBtn: (disabled: boolean) => void;
    dismissErrorMsg: () => void;
    getCriticality: () => Promise<CriticalityMap>;
    calculateRanking: (criticality: string) => Promise<void>;
    onSubmit: MutableRefObject<() => void>;
}
interface RankingColumn {
    id: string;
    title: string;
    score: number;
    attributes: AttributeSelectionOption[];
}

const initRankingColumns: RankingColumn[] = [{
    id: 'unranked',
    title: constants.APP_PRIORITIZATION_EDIT_STEP_2_UNRANKED,
    score: 0,
    attributes: [],
}, {
    id: 'leastRelevant',
    title: constants.APP_PRIORITIZATION_EDIT_STEP_2_LEAST_RELEVANT,
    score: 0.2,
    attributes: [],
}, {
    id: 'lessRelevant',
    title: constants.APP_PRIORITIZATION_EDIT_STEP_2_LESS_RELEVANT,
    score: 0.4,
    attributes: [],
}, {
    id: 'relevant',
    title: constants.APP_PRIORITIZATION_EDIT_STEP_2_RELEVANT,
    score: 0.6,
    attributes: [],
}, {
    id: 'moreRelevant',
    title: constants.APP_PRIORITIZATION_EDIT_STEP_2_MORE_RELEVANT,
    score: 0.8,
    attributes: [],
}, {
    id: 'mostRelevant',
    title: constants.APP_PRIORITIZATION_EDIT_STEP_2_MOST_RELEVANT,
    score: 1,
    attributes: [],
}];

const colSpan = [{ colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }];

const BusinessRanking = ({ attributes, setDisableSubmitBtn, dismissErrorMsg, getCriticality, calculateRanking, onSubmit, flash }: Props): JSX.Element => {
    const [ loading, setLoading ] = useState(false);
    const [ rankingCols, setRankingCols ] = useState<RankingColumn[]>(deepCopy(initRankingColumns));
    const isMounted = useIsMounted();

    useEffect(() => {
        const loadCriticalityMap = async () => {
            setLoading(true);
            try {
                const deepCopiedRankingCols: RankingColumn[] = deepCopy(initRankingColumns);
                const criticalityMap = await getCriticality();
                if (isMounted()) {
                    attributes.forEach(attr => {
                        const currentCriticality = criticalityMap[attr.value];
                        if (!currentCriticality) {
                            // add new attributes to unranked
                            deepCopiedRankingCols[0].attributes.push(attr);
                            return;
                        }
                        const currentCriticalityIndex = deepCopiedRankingCols.findIndex(col => col.score === currentCriticality);
                        currentCriticalityIndex > -1 ?
                            deepCopiedRankingCols[currentCriticalityIndex].attributes.push(attr) : deepCopiedRankingCols[0].attributes.push(attr);
                    });
                    setRankingCols(deepCopiedRankingCols);
                    setDisableSubmitBtn(isInvalidRanking(deepCopiedRankingCols));
                }
            } catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as Error).message, constants.APP_PRIORITIZATION_GET_CRITICALITY_ERROR);
                }
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }
            }
        };
        loadCriticalityMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const prioritizeApplication = async () => {
            const criticalityMap = rankingCols.reduce((criticality, col) => {
                const attributes = col.attributes;
                const score = col.score;
                attributes.forEach(attr => {
                    criticality = { ...criticality, [attr.value]: score };
                });
                return criticality;
            }, {} as CriticalityMap);
    
            await calculateRanking(JSON.stringify(criticalityMap));
        };

        onSubmit.current = prioritizeApplication;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ attributes, rankingCols ]);

    const isInvalidRanking = (ranks: RankingColumn[]): boolean => {
        return ranks[0].attributes.length > 0;
    };

    const handleDragEnd = (result: DropResult): void => {
        const source = result.source;
        const destination = result.destination;
        // dropped nowhere
        if (!destination) {
            return;
        }

        // did not move anywhere
        if (source.droppableId === destination.droppableId) {
            return;
        }

        const deepCopiedRankingCol: RankingColumn[] = deepCopy(rankingCols);
        const original = deepCopiedRankingCol.find(rankCol => rankCol.id === source.droppableId);
        const next = deepCopiedRankingCol.find(rankCol => rankCol.id === destination.droppableId);
        if (!original || !next) {
            return;
        }
        const target = original.attributes[source.index];
        original.attributes.splice(source.index, 1);
        next.attributes.splice(destination.index, 0, target);

        setRankingCols(deepCopiedRankingCol);
        dismissErrorMsg();
        setDisableSubmitBtn(isInvalidRanking(deepCopiedRankingCol));
    };

    const draggableItems = (attributes: AttributeSelectionOption[]): JSX.Element => (
        <div>
            {attributes.map((attr, index) => (
                <Draggable draggableId={attr.value} key={attr.value} index={index}>
                    {dragProvided => (
                        <div
                            className="drag-item"
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}>
                            <b>{attr.label}</b>
                        </div>
                    )}
                </Draggable>
            ))}
        </div>
    );

    const dragAndDropContext = (
        <DragDropContext onDragEnd={result => handleDragEnd(result)}>
            <Grid disableGutters gridDefinition={colSpan}>
                {rankingCols.map(rankingColumn => {
                    return (
                        <div key={`${rankingColumn.id}`}>
                            <div
                                id={`ranking-column-${rankingColumn.id}`}
                                className="ranking-column-headers">
                                <h3>{rankingColumn.title}</h3>
                            </div>
                            <Droppable droppableId={`${rankingColumn.id}`}>
                                {dropProvided => (
                                    <div
                                        className="drop-area"
                                        ref={dropProvided.innerRef}
                                        {...dropProvided.droppableProps}
                                    >
                                        {draggableItems(rankingColumn.attributes)}
                                        {dropProvided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    );
                })}
            </Grid>
        </DragDropContext>
    );

    return loading ? <Spinner size="large" /> : dragAndDropContext;
};

export default withFlashContext(BusinessRanking);
